import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CredentialResponse } from '@react-oauth/google';
import { setShowError } from '../store/error';
import { sendSignupTokenRequest, selectUuid, selectEmailConsent } from '../store/signup';
import { selectFlowType } from '../store/plans';
import { useEventNameConstructor, useSendEvents } from '.';
import { GOOGLE_CLIENT_ID, EVENTS as E } from '../constants';
import { setEventData } from '../store/events';

export const useGoogleAuth = (screenId: string = '') => {
  const dispatch = useDispatch();
  const sendEvents = useSendEvents({ screenId });
  const getEventName = useEventNameConstructor({ screenId });
  const user_uuid = useSelector(selectUuid);
  const consentEmail = useSelector(selectEmailConsent);
  const landing_type = useSelector(selectFlowType);
  const [isBtnPreloaded, setIsBtnPreloaded] = useState(false);
  const [googleScriptReady, setGoogleScriptReady] = useState(false);

  const handleGoogleAuth = useCallback(
    async (response: CredentialResponse) => {
      try {
        const token = response.credential;
        if (!token) {
          dispatch(
            setShowError(
              'Failed to create an account using Google, please try again or use the default email method.',
            ),
          );
          return;
        }
        const handleSuccess = (email: string) => {
          const eventParams = { email };
          window?.ttq?.track('Contact', {});
          sendEvents(getEventName(E.CLICK), {
            [E.BC]: 'continue',
            ...eventParams,
          });
          sendEvents(E.ENTER_EMAIL_SUBMITTED, eventParams);
          dispatch(setEventData(eventParams));
        };

        await sendSignupTokenRequest({
          token,
          user_uuid,
          landing_type,
          onSuccess: handleSuccess,
          consent_email: consentEmail,
        })(dispatch);
      } catch (error) {
        console.error('Google Authentication Error:', error);
        dispatch(
          setShowError(
            'An unexpected error occurred during Google authentication. Please try again.',
          ),
        );
      }
    },
    [dispatch, user_uuid, landing_type, consentEmail, sendEvents, getEventName],
  );

  useEffect(() => {
    const checkGoogleScript = () => {
      if (window.google?.accounts?.id) {
        setGoogleScriptReady(true);
        return true;
      }
      return false;
    };

    if (checkGoogleScript()) return;

    const interval = setInterval(() => {
      if (checkGoogleScript()) {
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsBtnPreloaded(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!isBtnPreloaded || !googleScriptReady) return;

    let cancelled = false;
    let buttonRetryTimer: ReturnType<typeof setTimeout> | undefined;

    try {
      if (!GOOGLE_CLIENT_ID) {
        dispatch(
          setShowError('Google Sign-In is not properly configured. Please try again later.'),
        );
        return;
      }

      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleAuth,
        ux_mode: 'popup',
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      const initButton = () => {
        if (cancelled) return;

        const buttonElement = document.getElementById('googleSignInButton');
        if (buttonElement) {
          window.google.accounts.id.renderButton(buttonElement, {
            type: 'standard',
            theme: 'outline',
            size: 'large',
            width: '343',
            text: 'continue_with',
            shape: 'rectangular',
            logo_alignment: 'left',
          });
        } else {
          buttonRetryTimer = setTimeout(initButton, 100);
        }
      };
      initButton();
    } catch (error) {
      dispatch(setShowError('Failed to initialize Google Sign-In. Please try again later.'));
    }

    return () => {
      cancelled = true;
      if (buttonRetryTimer) clearTimeout(buttonRetryTimer);
    };
  }, [isBtnPreloaded, googleScriptReady, handleGoogleAuth, dispatch]);

  const handleCustomButtonClick = () => {
    if (!isBtnPreloaded) {
      dispatch(
        setShowError('Google Sign-In is not ready yet. Please wait a moment and try again.'),
      );
      return;
    }

    sendEvents(`${screenId}__click`, { [E.BC]: 'google' });

    try {
      if (!window.google?.accounts?.id) {
        dispatch(setShowError('Google Sign-In is not available. Please try again later.'));
        return;
      }
      window.google.accounts.id.prompt();
    } catch (error) {
      dispatch(
        setShowError('An error occurred while trying to sign in with Google. Please try again.'),
      );
    }
  };

  return {
    isBtnPreloaded,
    googleScriptReady,
    handleCustomButtonClick,
  };
};
